/*
 * <cl26-price-slider> — dual-thumb price range slider for the 2026 collection grid.
 *
 * It is a thin UI layer over the theme's existing price inputs: it reads/writes the
 * hidden <price-range-selector> text inputs (data-price-range-min / -max, named
 * filter.v.price.gte / .lte), so the shared AJAX engine (component-filtering.js)
 * applies the filter with no changes. Drag updates the UI only; releasing a thumb
 * (the "change" event) commits the value and triggers a filtered re-render.
 */
(function () {
  class Cl26PriceSlider extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.min = parseFloat(this.dataset.min) || 0;
      this.max = parseFloat(this.dataset.max) || 0;
      this.step = parseFloat(this.dataset.step) || 1;
      this.symbol = this.dataset.currency || '';

      this.thumbMin = this.querySelector('[data-cl26-price-thumb-min]');
      this.thumbMax = this.querySelector('[data-cl26-price-thumb-max]');
      this.fill = this.querySelector('[data-cl26-price-fill]');
      this.labelMin = this.querySelector('[data-cl26-price-min-label]');
      this.labelMax = this.querySelector('[data-cl26-price-max-label]');

      const prs = this.querySelector('price-range-selector');
      this.inputMin = prs && prs.querySelector('[data-price-range-min]');
      this.inputMax = prs && prs.querySelector('[data-price-range-max]');

      if (!this.thumbMin || !this.thumbMax || !this.inputMin || !this.inputMax || this.max <= this.min) {
        return;
      }

      // The hidden text inputs are slider-driven; keep them out of the tab order
      // so keyboard users only reach the thumbs.
      this.inputMin.setAttribute('tabindex', '-1');
      this.inputMax.setAttribute('tabindex', '-1');

      // Seed thumbs from machine-readable whole-dollar attributes — the formatted
      // text inputs can carry thousands separators / comma decimals that
      // parseFloat would mangle (e.g. "1,200.00" -> 1).
      const seedMin = this.clampToStep(parseFloat(this.dataset.currentMin));
      const seedMax = this.clampToStep(parseFloat(this.dataset.currentMax));
      this.thumbMin.value = isNaN(seedMin) ? this.min : seedMin;
      this.thumbMax.value = isNaN(seedMax) ? this.max : seedMax;
      // Normalise the hidden inputs to clean whole-dollar values (empty at the
      // extremes) so every later read/serialize is unambiguous. No event fired.
      this.writeInputs(parseFloat(this.thumbMin.value), parseFloat(this.thumbMax.value));

      this.thumbMin.addEventListener('input', (e) => this.onThumb(e, 'min'));
      this.thumbMax.addEventListener('input', (e) => this.onThumb(e, 'max'));
      this.thumbMin.addEventListener('change', (e) => this.onThumb(e, 'min'));
      this.thumbMax.addEventListener('change', (e) => this.onThumb(e, 'max'));

      // Bring the grabbed thumb to the front so overlapping thumbs stay usable,
      // and commit the filter the moment a drag ends (pointer/touch release).
      // The "change" event covers keyboard + most drags; pointerup/touchend make
      // "done dragging" reliable everywhere. The form's debounce coalesces the
      // duplicate commits into a single filtered re-render, so this never
      // double-submits.
      [this.thumbMin, this.thumbMax].forEach((t) => {
        t.addEventListener('pointerdown', () => this.bringToFront(t));
        t.addEventListener('focus', () => this.bringToFront(t));
        t.addEventListener('pointerup', () => this.commitFromThumbs());
        t.addEventListener('touchend', () => this.commitFromThumbs());
      });

      // "Clear all" / per-filter reset links are re-bound after each AJAX render,
      // so listen on the document and reset our UI once the engine clears inputs.
      this._onDocClick = (e) => {
        if (e.target.closest && e.target.closest('.js-clear-all-filters, .price-filter-reset')) {
          setTimeout(() => this.reset(), 0);
        }
      };
      document.addEventListener('click', this._onDocClick);

      // After any filtered re-render, reconcile thumbs to the canonical hidden
      // inputs so the desktop and mobile sliders stay in step.
      this._onPaginate = () => this.syncThumbsFromInputs();
      document.addEventListener('paginate', this._onPaginate);

      this.render();
    }

    disconnectedCallback() {
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
      if (this._onPaginate) document.removeEventListener('paginate', this._onPaginate);
    }

    clampToStep(v) {
      if (isNaN(v)) return v;
      v = Math.round(v / this.step) * this.step;
      return Math.min(this.max, Math.max(this.min, v));
    }

    // Write whole-dollar values to every price input in the form (desktop +
    // mobile), empty at the extremes so no redundant param is submitted.
    writeInputs(lo, hi) {
      const minVal = lo <= this.min ? '' : lo;
      const maxVal = hi >= this.max ? '' : hi;
      const scope = this.closest('form') || this;
      scope.querySelectorAll('input[data-price-range-min]').forEach((i) => { i.value = minVal; });
      scope.querySelectorAll('input[data-price-range-max]').forEach((i) => { i.value = maxVal; });
    }

    syncThumbsFromInputs() {
      const lo = parseFloat(this.inputMin.value);
      const hi = parseFloat(this.inputMax.value);
      this.thumbMin.value = isNaN(lo) ? this.min : this.clampToStep(lo);
      this.thumbMax.value = isNaN(hi) ? this.max : this.clampToStep(hi);
      this.render();
    }

    bringToFront(t) {
      this.thumbMin.classList.toggle('cl26-price__thumb--front', t === this.thumbMin);
      this.thumbMax.classList.toggle('cl26-price__thumb--front', t === this.thumbMax);
    }

    onThumb(e, which) {
      // Keep the drag UI from triggering the form's debounced auto-submit; we
      // commit explicitly on "change".
      if (e.type === 'input') e.stopPropagation();

      let lo = parseFloat(this.thumbMin.value);
      let hi = parseFloat(this.thumbMax.value);
      if (which === 'min' && lo > hi) { lo = hi; this.thumbMin.value = lo; }
      if (which === 'max' && hi < lo) { hi = lo; this.thumbMax.value = hi; }

      this.render();
      if (e.type === 'change') this.commit(lo, hi);
    }

    render() {
      const lo = parseFloat(this.thumbMin.value);
      const hi = parseFloat(this.thumbMax.value);
      const span = this.max - this.min || 1;
      if (this.fill) {
        this.fill.style.left = ((lo - this.min) / span) * 100 + '%';
        this.fill.style.right = (100 - ((hi - this.min) / span) * 100) + '%';
      }
      if (this.labelMin) this.labelMin.textContent = this.symbol + lo;
      if (this.labelMax) this.labelMax.textContent = this.symbol + hi;
    }

    // Read the current (already ordered) thumb values and commit them — used by
    // the pointerup/touchend "drag end" handlers.
    commitFromThumbs() {
      this.commit(parseFloat(this.thumbMin.value), parseFloat(this.thumbMax.value));
    }

    commit(lo, hi) {
      this.writeInputs(lo, hi);
      const form = this.closest('form');
      if (form) form.dispatchEvent(new Event('input', { bubbles: true }));
    }

    reset() {
      this.thumbMin.value = this.min;
      this.thumbMax.value = this.max;
      this.writeInputs(this.min, this.max); // both extremes -> cleared
      this.render();
    }
  }

  if (!customElements.get('cl26-price-slider')) {
    customElements.define('cl26-price-slider', Cl26PriceSlider);
  }
})();
